/**
 * Workspace-scoped commands: equipment profile CRUD and settings.
 *
 * Built-in profiles are read-only. `equipment.update` and `equipment.delete`
 * refuse them with `invalid-state`, and the escape hatch is
 * `equipment.create({ from: builtinProfile })` — duplicate, then edit. That
 * keeps the shipped presets identical on every device and every replay, which
 * matters because they are part of the deterministic seed state.
 *
 * Deleting a profile does **not** rewrite the recipes that point at it. A
 * dangling `equipmentProfileId` resolves through
 * {@link resolveEquipmentProfileDoc} to the workspace default, so delete stays
 * a one-document change and therefore exactly invertible.
 */

import { BUILTIN_EQUIPMENT_PROFILES, type EquipmentProfileDoc } from '../../document/equipment';
import type { UnitPreferences, WorkspaceState } from '../../document/workspace';
import { UNIT_SYSTEM_PRESETS, unitSystemOf } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/** The profile a new equipment document starts from when the caller supplies no template. */
const EQUIPMENT_TEMPLATE = BUILTIN_EQUIPMENT_PROFILES[0];

/** A copy of `state` with one equipment profile replaced or added. */
function putProfile(state: WorkspaceState, profile: EquipmentProfileDoc): WorkspaceState {
  return {
    ...state,
    equipmentProfiles: { ...state.equipmentProfiles, [profile.id]: profile },
  };
}

/** Create an equipment profile, optionally by duplicating an existing one. */
export const equipmentCreate = defineCommand({
  type: 'equipment.create',
  payload: p.equipmentCreatePayloadSchema,
  apply: (state, command) => {
    const { profileId, name, from } = command.payload;
    if (state.equipmentProfiles[profileId] !== undefined) {
      return {
        ok: false,
        error: commandError(
          'conflict',
          `An equipment profile with id "${profileId}" already exists.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const template = from ?? EQUIPMENT_TEMPLATE;
    if (template === undefined) {
      return {
        ok: false,
        error: commandError('invalid-state', 'No equipment template available.', {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const profile: EquipmentProfileDoc = {
      ...template,
      id: profileId,
      name,
      builtin: false,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putProfile(state, profile) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'equipment.delete',
      { profileId: command.payload.profileId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Create equipment profile ${quoted(command.payload.name)}`,
});

/** Change fields of an equipment profile. Built-ins refuse. */
export const equipmentUpdate = defineCommand({
  type: 'equipment.update',
  payload: p.equipmentUpdatePayloadSchema,
  apply: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No equipment profile with id "${command.payload.profileId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    if (profile.builtin) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `${quoted(profile.name)} is a built-in profile. Duplicate it before editing.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putProfile(state, {
        ...mergeDefined(profile, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(
          command,
          'equipment.update',
          {
            profileId: profile.id,
            changes: priorValues(profile, command.payload.changes),
          },
          profile.updatedAt,
        );
  },
  summarize: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update equipment profile ${quoted(profile?.name ?? command.payload.profileId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete an equipment profile. Built-ins refuse. */
export const equipmentDelete = defineCommand({
  type: 'equipment.delete',
  payload: p.equipmentDeletePayloadSchema,
  apply: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No equipment profile with id "${command.payload.profileId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    if (profile.builtin) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `${quoted(profile.name)} is a built-in profile and cannot be deleted.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const equipmentProfiles = { ...state.equipmentProfiles };
    delete equipmentProfiles[command.payload.profileId];
    return { ok: true, state: { ...state, equipmentProfiles } };
  },
  invert: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(command, 'equipment.restore', { profile }, profile.updatedAt);
  },
  summarize: (state, command) => {
    const profile = state.equipmentProfiles[command.payload.profileId];
    return `Delete equipment profile ${quoted(profile?.name ?? command.payload.profileId)}`;
  },
});

/** Insert an equipment profile verbatim — an upsert, and the inverse of `equipment.delete`. */
export const equipmentRestore = defineCommand({
  type: 'equipment.restore',
  payload: p.equipmentRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putProfile(state, command.payload.profile) }),
  invert: (state, command) => {
    const prior = state.equipmentProfiles[command.payload.profile.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'equipment.delete',
          { profileId: command.payload.profile.id },
          command.timestamp,
        )
      : inverseCommand(command, 'equipment.restore', { profile: prior }, prior.updatedAt);
  },
  summarize: (_state, command) =>
    `Restore equipment profile ${quoted(command.payload.profile.name)}`,
});

/** Change workspace settings. See the payload's TSDoc for the unit-system interaction. */
export const settingsUpdate = defineCommand({
  type: 'settings.update',
  payload: p.settingsUpdatePayloadSchema,
  apply: (state, command) => {
    const { unitSystem, units, brewAlerts, ...rest } = command.payload;
    const base: UnitPreferences =
      unitSystem === undefined || unitSystem === 'mixed'
        ? state.settings.units
        : UNIT_SYSTEM_PRESETS[unitSystem];
    const nextUnits = units === undefined ? { ...base } : mergeDefined(base, units);
    return {
      ok: true,
      state: {
        ...state,
        settings: {
          ...mergeDefined(state.settings, rest),
          units: nextUnits,
          unitSystem: unitSystemOf(nextUnits),
          // Merged key by key rather than written in whole: `mergeDefined` is
          // shallow, and each of the two Settings rows sends only its own
          // number. Its inverse still carries the full prior pair, which is
          // what `priorValues` hands back below.
          brewAlerts:
            brewAlerts === undefined
              ? state.settings.brewAlerts
              : mergeDefined(state.settings.brewAlerts, brewAlerts),
        },
      },
    };
  },
  invert: (state, command) => {
    const { unitSystem, units, brewAlerts, ...rest } = command.payload;
    const touchesUnits = unitSystem !== undefined || units !== undefined;
    return inverseCommand(
      command,
      'settings.update',
      {
        ...priorValues(state.settings, rest),
        // The whole prior pair, not just the half that changed: undoing has to
        // put back a state, and a partial would leave the other number at
        // whatever the command left it.
        ...(brewAlerts === undefined ? {} : { brewAlerts: { ...state.settings.brewAlerts } }),
        ...(touchesUnits
          ? { unitSystem: state.settings.unitSystem, units: { ...state.settings.units } }
          : {}),
      },
      command.timestamp,
    );
  },
  summarize: (_state, command) => {
    const keys = Object.keys(command.payload).sort();
    return keys.length === 0 ? 'Update settings' : `Update settings (${keys.join(', ')})`;
  },
});
